import { useEffect, useRef } from 'react';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { VideoFrameDecoder } from '@/video/videoFrameDecoder.ts';
import { diagLog } from '@/bridge/embeddedBridge.ts';
import {
  paintVideoMirror, setVideoMirrorSource, sizeVideoMirror, type VideoMirrorRole,
} from '@/video/videoMirror.ts';

/**
 * Syncs a VideoFrameDecoder to timeline playback, painting hardware-decoded
 * frames onto a <canvas> via drawImage(VideoFrame).
 *
 * Uses mp4box.js + WebCodecs VideoDecoder — completely bypasses the <video>
 * element and its iOS WKWebView user-gesture restrictions.
 *
 * When canvasRef is null, the hook creates a decoder and seeks (pre-seek mode
 * for warming upcoming shots) but skips drawing.
 *
 * 20260929 - `mirrorRole` (opt-in): the Viewer's two PAINTED calls pass 'primary' / 'transition', and
 * every frame this decoder paints is also handed to the laptop pose's playback mirror for that role
 * (video/videoMirror.ts) — one decode, two pictures, exact sync. Never passed by the next-pair
 * pre-seek or by FullscreenViewer. Without it the hook behaves exactly as before.
 */
export function useVideoSync(
  canvasRef: React.RefObject<HTMLCanvasElement | null> | null,
  videoData: ArrayBuffer | null,
  shotStartTime: number,
  shotDuration: number,
  onDimensions?: (width: number, height: number) => void,
  mirrorRole?: VideoMirrorRole,
) {
  const currentTime = usePlaybackStore(s => s.currentTime);
  const decoderRef = useRef<VideoFrameDecoder | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  // 20260929 - the mirror fan-out's role, read through a ref by the frame callback (which is created
  // once per decoder), and this canvas registered as the role's source so a mirror mounting while
  // paused can blit the frame already on screen.
  const mirrorRoleRef = useRef(mirrorRole);
  mirrorRoleRef.current = mirrorRole;
  useEffect(() => {
    if (!mirrorRole || !canvasRef) return;
    return setVideoMirrorSource(mirrorRole, canvasRef);
  }, [mirrorRole, canvasRef]);

  // Stable callback ref to avoid re-creating decoder when onDimensions changes
  const onDimensionsRef = useRef(onDimensions);
  onDimensionsRef.current = onDimensions;

  // Stable refs so the lifecycle effect can do an initial seek without
  // adding shotStartTime/shotDuration as dependencies (which would
  // destroy and re-create the decoder on every shot change)
  const shotStartTimeRef = useRef(shotStartTime);
  shotStartTimeRef.current = shotStartTime;
  const shotDurationRef = useRef(shotDuration);
  shotDurationRef.current = shotDuration;

  // ── Decoder lifecycle: create/destroy when videoData changes ──────
  useEffect(() => {
    // Clean up previous decoder
    if (decoderRef.current) {
      decoderRef.current.close();
      decoderRef.current = null;
      ctxRef.current = null;
    }

    if (!videoData) {
      diagLog('VideoSync', 'videoData is null — no video to decode');
      return;
    }

    diagLog('VideoSync', `Creating decoder — ${videoData.byteLength} bytes, canvas=${!!canvasRef?.current}`);

    const decoder = new VideoFrameDecoder(videoData);
    decoder.onLog = (msg) => diagLog('Decoder', msg);
    decoder.flushEarlyLogs();
    decoderRef.current = decoder;

    diagLog('VideoSync', `Decoder created — ready=${decoder.ready}, ${decoder.width}x${decoder.height}, duration=${decoder.duration}`);

    // Set up canvas dimensions and context when decoder is ready
    const canvas = canvasRef?.current;
    if (decoder.ready && canvas) {
      canvas.width = decoder.width;
      canvas.height = decoder.height;
      ctxRef.current = canvas.getContext('2d');
      onDimensionsRef.current?.(decoder.width, decoder.height);
      diagLog('VideoSync', `Canvas configured — ${canvas.width}x${canvas.height}`);
    } else {
      diagLog('VideoSync', `Canvas NOT configured — ready=${decoder.ready}, canvas=${!!canvas}`);
    }
    // 20260929 - a new decoder is a new shot: the mirror's canvas takes its size (and is cleared by
    // it), exactly as the Viewer's canvas is just above.
    if (decoder.ready && mirrorRoleRef.current) {
      sizeVideoMirror(mirrorRoleRef.current, decoder.width, decoder.height);
    }

    // Set up frame callback: draw to canvas.
    // The decoder holds the frame — do NOT close it here (decoder manages lifecycle).
    decoder.onFrame = (frame: VideoFrame) => {
      const ctx = ctxRef.current;
      const cvs = canvasRef?.current;
      if (ctx && cvs) {
        ctx.drawImage(frame, 0, 0, cvs.width, cvs.height);
      }
      // 20260929 - the same frame, same tick, into the laptop pose's mirror (a no-op unless the mirror
      // has registered a canvas for this role).
      const role = mirrorRoleRef.current;
      if (role) paintVideoMirror(role, frame, decoder.width, decoder.height);
    };

    // Initial seek: the per-tick render effect depends on [currentTime] and
    // won't re-fire if currentTime hasn't changed since the fetch started.
    // Seek now so the decoder paints the correct frame immediately.
    if (decoder.ready) {
      const ct = usePlaybackStore.getState().currentTime;
      const localTime = ct - shotStartTimeRef.current;
      if (localTime >= 0 && localTime <= shotDurationRef.current) {
        const seekTime = Math.min(localTime, decoder.duration);
        diagLog('VideoSync', `Initial seek — localTime=${localTime.toFixed(2)}, seekTime=${seekTime.toFixed(2)}, duration=${decoder.duration}`);
        decoder.seek(seekTime);
      }
    }

    return () => {
      decoder.close();
      if (decoderRef.current === decoder) {
        decoderRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoData]);

  // ── Per-tick rendering ───────────────────────────────────────────
  useEffect(() => {
    const decoder = decoderRef.current;
    if (!decoder || !decoder.ready) return;

    const localTime = currentTime - shotStartTime;
    if (localTime < 0 || localTime > shotDuration) return;

    // Clamp to video duration so we seek to the last frame when the shot
    // extends beyond the actual video length (hold-last-frame behavior)
    const seekTime = Math.min(localTime, decoder.duration);

    // Ensure canvas context is set (may not have been set during decoder init
    // if canvas wasn't mounted yet)
    if (!ctxRef.current && canvasRef?.current) {
      const canvas = canvasRef.current;
      canvas.width = decoder.width;
      canvas.height = decoder.height;
      ctxRef.current = canvas.getContext('2d');
      onDimensionsRef.current?.(decoder.width, decoder.height);
    }

    // seek() handles both forward (buffer-based) and backward (reset) cases
    decoder.seek(seekTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, shotStartTime, shotDuration]);
}
